import React, { useState, useEffect, useRef } from 'react';
import { Clock, CheckCircle2, Lock, AlertTriangle, Play, FlaskConical } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../services/api';

const FocusedLearningModal = ({ 
  isOpen, 
  onClose, 
  dailyRep, 
  onComplete,
  userId 
}) => {
  const [timeSpent, setTimeSpent] = useState(0);
  const [canClose, setCanClose] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showExitWarning, setShowExitWarning] = useState(false);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  
  // Minimum time in seconds (10 minutes = 600 seconds)
  const MIN_TIME_REQUIRED = 600;
  
  // IXL URL generation
  const ixlUrl = dailyRep?.ixl_code 
    ? `https://www.ixl.com/math/skill-shortcuts/${dailyRep.ixl_code}`
    : null;

  useEffect(() => {
    if (isOpen && dailyRep) {
      // Start timer when modal opens
      startTimeRef.current = Date.now();
      setTimeSpent(0);
      setCanClose(false);
      
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setTimeSpent(elapsed);
        
        if (elapsed >= MIN_TIME_REQUIRED) {
          setCanClose(true);
        }
      }, 1000);
      
      // Log session start
      logSessionEvent('start');
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isOpen, dailyRep]);

  const logSessionEvent = async (event) => {
    try {
      await api.post('/academy/ixl-session/log', {
        user_id: userId,
        day_number: dailyRep?.day_number,
        ixl_code: dailyRep?.ixl_code,
        event: event,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to log session event:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimeRemaining = () => {
    const remaining = Math.max(0, MIN_TIME_REQUIRED - timeSpent);
    const mins = Math.floor(remaining / 60);
    const secs = remaining % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAttemptClose = () => {
    if (!canClose) {
      setShowExitWarning(true);
      return;
    }
    handleCompleteAndClose();
  };

  const handleCompleteAndClose = async () => {
    setIsCompleting(true);
    
    try {
      // Log session completion
      await logSessionEvent('complete');
      
      // Call completion API
      const response = await api.post('/academy/daily-rep/complete', {
        day_number: dailyRep.day_number,
        time_spent: timeSpent,
        ixl_code: dailyRep.ixl_code
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('academy_token')}`
        }
      });
      
      if (response.data.success) {
        toast.success('Day Complete! Great work today. 💪');
        onComplete(response.data);
      }
    } catch (error) {
      console.error('Failed to complete daily rep:', error);
      toast.error('Failed to save progress. Please try again.');
    } finally {
      setIsCompleting(false);
      onClose();
    }
  };

  const handleForceExit = () => {
    // Log early exit
    logSessionEvent('early_exit');
    setShowExitWarning(false);
    onClose();
  };

  if (!isOpen || !dailyRep) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={handleAttemptClose}
      />
      
      {/* Modal */}
      <div className="relative w-full h-full max-w-6xl max-h-[90vh] mx-4 bg-gray-900 rounded-xl overflow-hidden border border-orange-500/30 shadow-2xl shadow-orange-500/10 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-gray-900 to-gray-800 border-b border-gray-800">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <Play className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">
                Day {dailyRep.day_number}: {dailyRep.title}
              </h2>
              <p className="text-gray-400 text-sm flex items-center gap-2">
                <span className="px-2 py-0.5 bg-orange-500/20 text-orange-500 rounded text-xs font-mono">
                  {dailyRep.ixl_code}
                </span>
                <span>{dailyRep.category}</span>
              </p>
            </div>
          </div>
          
          {/* Timer & Close */}
          <div className="flex items-center gap-4">
            {/* Time Spent */}
            <div className="text-right">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Clock className="w-4 h-4" />
                <span>Time in Session</span>
              </div>
              <p className={`font-mono text-xl ${canClose ? 'text-green-500' : 'text-orange-500'}`}>
                {formatTime(timeSpent)}
              </p>
            </div>
            
            {/* Close Lab Button */}
            <Button
              onClick={handleAttemptClose}
              disabled={isCompleting}
              className={`flex items-center gap-2 font-bold ${
                canClose 
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="close-lab-btn"
            >
              {isCompleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : canClose ? (
                <>
                  <FlaskConical className="w-4 h-4" />
                  Close Lab
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  {formatTimeRemaining()} remaining
                </>
              )}
            </Button>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="h-1 bg-gray-800">
          <div 
            className="h-full bg-gradient-to-r from-orange-500 to-green-500 transition-all duration-1000"
            style={{ width: `${Math.min(100, (timeSpent / MIN_TIME_REQUIRED) * 100)}%` }}
          />
        </div>
        
        {/* IXL iFrame */}
        <div className="flex-1 relative bg-white">
          {ixlUrl ? (
            <iframe
              src={ixlUrl}
              title={`IXL - ${dailyRep.title}`}
              className="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin allow-forms"
              allow="fullscreen"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-800">
              <div className="text-center">
                <AlertTriangle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
                <p className="text-white font-bold">IXL Code Not Found</p>
                <p className="text-gray-400 text-sm">Please contact support</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-3 bg-gray-800 border-t border-gray-700">
          <p className="text-gray-500 text-xs text-center">
            Complete at least 10 minutes of focused practice to unlock the check-in button. 
            Your progress is being tracked.
          </p>
        </div>
      </div>
      
      {/* Exit Warning Modal */}
      {showExitWarning && (
        <div className="absolute inset-0 z-60 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={() => setShowExitWarning(false)} />
          <div className="relative bg-gray-900 border border-red-500/50 rounded-xl p-6 max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                Wait! You're Not Done Yet
              </h3>
              <p className="text-gray-400 mb-6">
                You need to spend at least <span className="text-orange-500 font-bold">10 minutes</span> in your daily rep to check in. 
                You've only been here for <span className="text-orange-500 font-bold">{formatTime(timeSpent)}</span>.
              </p>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowExitWarning(false)}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-black font-bold"
                >
                  Continue Learning
                </Button>
                <Button
                  onClick={handleForceExit}
                  variant="outline"
                  className="flex-1 border-gray-700 text-gray-400 hover:bg-gray-800"
                >
                  Exit Anyway
                </Button>
              </div>
              
              <p className="text-red-400 text-xs mt-4">
                ⚠️ Exiting early will not count toward your daily progress
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FocusedLearningModal;
